import {
  ArrowUpRight,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Clock3,
  IndianRupee,
  Laptop2,
  MapPin,
  Sparkles,
} from "lucide-react";
import { createElement } from "react";
import { BookmarkIcon, BookmarkFilledIcon } from "./BookmarkIcon";

const OpportunityCard = ({ opportunity, type, isSaved, onToggleBookmark, showBookmark = true }) => {
  const isInternship = type === "internship";
  const isHackathon = type === "hackathon";
  const isPlacement = type === "placement";

  const typeClass = isInternship ? "badge-blue" : isHackathon ? "badge-green" : "badge-purple";
  const typeLabel = isInternship ? "Internship" : isHackathon ? "Hackathon" : "Placement";
  const TypeIcon = isInternship ? BriefcaseBusiness : isHackathon ? Sparkles : Building2;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatStipend = (amount) => amount ? `₹${amount.toLocaleString("en-IN")}/month` : "Not specified";
  const formatSalary = (amount) => {
    if (!amount) return "Not specified";
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} LPA`;
    return `₹${amount.toLocaleString("en-IN")}/month`;
  };

  return (
    <article className="card group">
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-indigo-50 group-hover:text-indigo-600">
          {createElement(TypeIcon, { size: 20 })}
        </span>
        <span className={`badge ${typeClass}`}>{typeLabel}</span>
      </div>

      <div className="mb-4 min-w-0">
        <h3 className="line-clamp-2 text-lg font-bold leading-6 text-slate-900 transition-colors group-hover:text-indigo-700">
          {opportunity.title}
        </h3>
        <p className="mt-1 truncate text-sm font-medium text-slate-500">
          {opportunity.company || opportunity.organizer || "Independent organiser"}
        </p>
      </div>

      <div className="mb-5 space-y-2.5">
        {opportunity.location && (
          <Meta icon={MapPin}>
            <span className="truncate">{opportunity.location}</span>
            {opportunity.remote && <span className="badge badge-green ml-1">Remote</span>}
          </Meta>
        )}

        {isInternship && (
          <>
            <Meta icon={IndianRupee}>{formatStipend(opportunity.stipend)}</Meta>
            {opportunity.duration && <Meta icon={Clock3}>{opportunity.duration} months</Meta>}
          </>
        )}

        {isPlacement && <Meta icon={IndianRupee}>{formatSalary(opportunity.salary || opportunity.stipend)}</Meta>}

        {isHackathon && opportunity.prize && (
          <Meta icon={Sparkles}>Prize: ₹{opportunity.prize.toLocaleString("en-IN")}</Meta>
        )}

        {isHackathon && opportunity.mode && <Meta icon={Laptop2}>{opportunity.mode}</Meta>}

        {opportunity.deadline && (
          <Meta icon={CalendarDays} className="font-semibold text-orange-600">
            Apply by {formatDate(opportunity.deadline)}
          </Meta>
        )}

        {opportunity.source && <span className="badge badge-blue">Via {opportunity.source}</span>}
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-4">
        <a
          href={opportunity.applyLink || opportunity.link}
          target="_blank"
          rel="noopener noreferrer"
          className="button btn-primary text-sm"
        >
          {isHackathon ? "View details" : "Apply now"}
          <ArrowUpRight size={16} className="ml-1.5" />
        </a>

        {showBookmark && onToggleBookmark && (
          <button
            onClick={() => onToggleBookmark(opportunity.id)}
            aria-label={isSaved ? "Remove from saved opportunities" : "Save opportunity"}
            className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-all ${
              isSaved
                ? "border-indigo-100 bg-indigo-50 text-indigo-600"
                : "border-slate-200 text-slate-400 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
            }`}
          >
            {isSaved ? <BookmarkFilledIcon /> : <BookmarkIcon />}
          </button>
        )}
      </div>
    </article>
  );
};

const Meta = ({ icon: Icon, children, className = "" }) => (
  <div className={`flex min-w-0 items-center gap-2 text-sm text-slate-600 ${className}`}>
    {createElement(Icon, { size: 16, className: "shrink-0 text-slate-400" })}
    {children}
  </div>
);

export default OpportunityCard;
